import { SmallText, Text } from '@shared/designSystem/Texts'
import StatusDot from '@shared/designSystem/StatusDot'
import designSystemStyles from '@shared/styles/designSystemStyles'
import styles from '@shared/styles/components/FilterPillGroup.module.css'

export interface FilterPillOption {
    id: string
    label: string
    count?: number
    // Optional status dot — renders only when dotColor is provided.
    // Pass a DS token value (e.g. designSystemStyles.colorSuccess); omit for no dot.
    dotColor?: string
    isDisabled?: boolean
}

interface FilterPillGroupProps {
    options: FilterPillOption[]
    selectedId: string
    onSelect: (id: string) => void
    ariaLabel?: string
    className?: string
    // When false, pills stay on a single line instead of wrapping. Defaults to true.
    wrap?: boolean
    // While true the pills are non-interactive and each count renders as a skeleton
    // shimmer. Labels stay visible so the layout doesn't jump when data lands.
    isLoading?: boolean
    // 'small' tightens the padding and uses the small text style, for dense layouts
    // (toolbars, modals). Defaults to 'regular'.
    size?: 'regular' | 'small'
}

const FilterPillGroup: React.FC<FilterPillGroupProps> = ({
    options,
    selectedId,
    onSelect,
    ariaLabel,
    className,
    wrap = true,
    isLoading = false,
    size = 'small',
}) => {
    const PillText = size === 'small' ? SmallText : Text
    const enabledIds = options.filter((option) => !option.isDisabled).map((option) => option.id)

    const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
        if (isLoading) {
            return
        }
        if (event.key !== 'ArrowRight' && event.key !== 'ArrowLeft') {
            return
        }

        const currentIndex = enabledIds.indexOf(selectedId)
        if (currentIndex === -1 || enabledIds.length === 0) {
            return
        }

        event.preventDefault()
        const delta = event.key === 'ArrowRight' ? 1 : -1
        const nextIndex = (currentIndex + delta + enabledIds.length) % enabledIds.length
        onSelect(enabledIds[nextIndex])
    }

    return (
        <div
            role="radiogroup"
            aria-label={ariaLabel}
            aria-busy={isLoading || undefined}
            onKeyDown={handleKeyDown}
            className={['DSFilterPillGroup', styles.group, wrap ? '' : styles.noWrap, className].filter(Boolean).join(' ')}
        >
            {options.map((option) => {
                const isSelected = option.id === selectedId
                const isInteractionBlocked = option.isDisabled || isLoading

                return (
                    <button
                        key={option.id}
                        type="button"
                        role="radio"
                        aria-checked={isSelected}
                        aria-disabled={isInteractionBlocked}
                        disabled={isInteractionBlocked}
                        tabIndex={isSelected ? 0 : -1}
                        onClick={() => !isInteractionBlocked && onSelect(option.id)}
                        className={[
                            styles.pill,
                            size === 'small' ? styles.small : '',
                            isSelected ? styles.selected : '',
                            option.isDisabled ? styles.disabled : '',
                            isLoading ? styles.loading : '',
                        ]
                            .filter(Boolean)
                            .join(' ')}
                    >
                        {option.dotColor ? <StatusDot color={option.dotColor} /> : null}
                        <PillText
                            text={isSelected ? `**${option.label.trim()}**` : option.label}
                            markdown={isSelected}
                            color={isSelected ? designSystemStyles.colorMainAction : designSystemStyles.colorTextSecondary}
                        />
                        {isLoading && option.count !== undefined ? (
                            <span className={styles.skeletonCount} />
                        ) : option.count !== undefined ? (
                            <PillText
                                text={String(option.count)}
                                color={isSelected ? designSystemStyles.colorMainAction : designSystemStyles.colorText}
                            />
                        ) : null}
                    </button>
                )
            })}
        </div>
    )
}

export default FilterPillGroup
